import Vue from 'vue'
import VueRouter from 'vue-router'

let initPromise = null
let SentryInstance = null
const ROUTER_ERROR_HANDLER_INSTALLED = '__sentryRouterErrorHandlerInstalled__'

// 错误队列：在 sentry 初始化前收集错误
const errorQueue = []

// 保存原始错误处理器和事件监听器的引用
let originalErrorHandler = null
let unhandledRejectionListener = null

/**
 * 将错误添加到队列
 */
function queueError(errorData) {
    errorQueue.push({
        ...errorData,
        timestamp: Date.now(),
    })
    // 限制队列大小，避免内存泄漏
    if (errorQueue.length > 50) {
        errorQueue.shift()
    }
}

/**
 * 上报队列中的错误
 */
async function flushErrorQueue() {
    if (!SentryInstance || errorQueue.length === 0) {
        return
    }

    // 批量上报队列中的错误
    const errors = errorQueue.splice(0)
    errors.forEach(errorData => {
        try {
            if (errorData.type === 'error') {
                // JavaScript 错误
                SentryInstance.captureException(errorData.error || new Error(errorData.message), {
                    tags: {
                        queued: true,
                        originalSource: errorData.source,
                        originalLineno: errorData.lineno,
                        originalColno: errorData.colno,
                    },
                    extra: {
                        queuedAt: errorData.timestamp,
                        initializedAt: Date.now(),
                    },
                })
            } else if (errorData.type === 'unhandledrejection') {
                // Promise 拒绝
                SentryInstance.captureException(errorData.reason, {
                    tags: {
                        queued: true,
                        type: 'unhandledrejection',
                    },
                    extra: {
                        queuedAt: errorData.timestamp,
                        initializedAt: Date.now(),
                    },
                })
            } else if (errorData.type === 'vue-router-async') {
                // Vue Router 异步组件加载错误
                SentryInstance.captureException(errorData.error, {
                    tags: {
                        queued: true,
                        source: 'vue-router-async',
                    },
                    extra: {
                        message: 'vue-router执行异常，请检查路由组件和模块是否执行正常。',
                        value: errorData.error,
                        queuedAt: errorData.timestamp,
                        initializedAt: Date.now(),
                    },
                })
            }
        } catch (e) {
            console.error('Failed to report queued error:', e)
        }
    })
}

/**
 * 初始化 Sentry（懒加载）
 * 延迟加载包，但提前设置错误监听
 */
async function initSentry(Vue) {
    // 如果已经初始化或正在初始化，直接返回
    if (SentryInstance) {
        return SentryInstance
    }
    if (initPromise) {
        return initPromise
    }

    // 创建初始化 Promise
    initPromise = (async () => {
        try {
            const Sentry = await import('@sentry/vue')
            const { registerSentry, SentryEnv } = await import('@fs/sentry')
            registerSentry(Sentry, {
                Vue,
                environment: SentryEnv[process.env.VUE_APP_ENV],
                tracesSampleRate: 1.0,
                replaysOnErrorSampleRate: 1.0,
                replaysSessionSampleRate: 0.1,
                integrations: [
                    Sentry.browserTracingIntegration(),
                    Sentry.replayIntegration({
                        maskAllText: true,
                        blockAllUrls: true,
                    }),
                    Sentry.replayCanvasIntegration({
                        enableManualSnapshot: true,
                    }),
                ],
            })
            SentryInstance = Sentry

            // 上报队列中的错误
            await flushErrorQueue()

            // Sentry 初始化完成后，恢复原始错误处理器
            restoreOriginalErrorHandlers()
        } catch (error) {
            console.error('Failed to initialize Sentry:', error)
            initPromise = null // 重置，允许重试
        }
    })()

    return initPromise
}

/**
 * 恢复原始错误处理器
 * Sentry 初始化完成后调用，让 Sentry 接管错误处理
 */
function restoreOriginalErrorHandlers() {
    if (typeof window === 'undefined') {
        return
    }

    // 恢复 window.onerror
    if (originalErrorHandler !== null) {
        window.onerror = originalErrorHandler
    } else {
        window.onerror = null
    }

    // 移除 unhandledrejection 事件监听器
    // 注意：unhandledrejection 只能通过 addEventListener 监听，没有 window.onunhandledrejection
    if (unhandledRejectionListener) {
        window.removeEventListener('unhandledrejection', unhandledRejectionListener)
        unhandledRejectionListener = null
    }
}

/**
 * 提前设置错误监听（在 sentry 初始化前）
 * 这样可以捕获初始化前的所有错误
 */
function setupErrorHandlers() {
    if (typeof window === 'undefined') {
        return
    }

    // 保存原始错误处理器
    originalErrorHandler = window.onerror

    // 捕获 JavaScript 错误
    // window.onerror 是标准 API，有 5 个参数
     
    window.onerror = function (message, source, lineno, colno, error) {
        if (!SentryInstance) {
            // 否则加入队列
            queueError({
                type: 'error',
                message,
                source,
                lineno,
                colno,
                error,
            })
            if (!initPromise) {
                initSentry(Vue)
            }
        }

        // 调用原始错误处理器
        if (originalErrorHandler) {
            return originalErrorHandler.call(this, message, source, lineno, colno, error)
        }
    }

    // 捕获 Promise 未处理的拒绝
    // 注意：unhandledrejection 只能通过 addEventListener 监听，没有 window.onunhandledrejection
    unhandledRejectionListener = function (event) {
        if (!SentryInstance) {
            // 否则加入队列
            queueError({
                type: 'unhandledrejection',
                reason: event.reason,
            })
            if (!initPromise) {
                initSentry(Vue)
            }
        }
        // Sentry 初始化后，事件会继续传播，Sentry 会自动捕获
    }
    window.addEventListener('unhandledrejection', unhandledRejectionListener)
}

function captureRouterError(error) {
    const options = {
        tags: { source: 'vue-router-async' },
        extra: { message: 'vue-router执行异常，请检查路由组件和模块是否执行正常。', value: error },
    }

    if (SentryInstance) {
        SentryInstance.captureException(error, options)
        return
    }

    queueError({
        type: 'vue-router-async',
        error,
    })
    if (!initPromise) {
        initSentry(Vue)
    }
}

function registerRouterErrorHandler(router) {
    if (!router || router[ROUTER_ERROR_HANDLER_INSTALLED]) {
        return
    }

    router[ROUTER_ERROR_HANDLER_INSTALLED] = true
    router.onError(err => {
        captureRouterError(err)
    })
}

function setupRouterErrorHandler() {
    const originalInit = VueRouter.prototype.init

    if (originalInit.__sentryPatched__) {
        return
    }

    VueRouter.prototype.init = function init(app) {
        registerRouterErrorHandler(this)
        return originalInit.call(this, app)
    }
    VueRouter.prototype.init.__sentryPatched__ = true
}

function lazyInitSentry() {
    // 监听页面加载完成事件，作为备用方案
    if (document.readyState !== 'complete') {
        const onLoad = () => {
            setTimeout(() => {
                initSentry(Vue)
            }, 500)
            window.removeEventListener('load', onLoad)
        }
        window.addEventListener('load', onLoad, { once: true })
    } else {
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(
                () => {
                    initSentry(Vue)
                },
                { timeout: 2000 } // 最多等待 2 秒
            )
        } else {
            setTimeout(() => {
                initSentry(Vue)
            }, 1000)
        }
    }
}

setupErrorHandlers()
setupRouterErrorHandler()

lazyInitSentry()

export { initSentry, registerRouterErrorHandler }
